import React, { useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import BuyButton from './BuyButton';
import { usePayment } from '../contexts/PaymentContext';

const Header = ({ onNavigateToDashboard, onNavigateToDesignInspiration, onNavigateToHome, onNavigateToProfile, onNavigateToDownloadablePrompts }) => {
  const { hasAccess, accessTier } = usePayment();
  const showBuyCta = !hasAccess || accessTier === 'trial';
  const { scrollY: motionScrollY } = useScroll();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Transform scroll position to opacity (fade out when scrolling down)
  const opacity = useTransform(motionScrollY, [0, 200], [1, 0.3]);
  const y = useTransform(motionScrollY, [0, 200], [0, -10]);

  return (
    <motion.header 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      style={{ opacity, y }}
      className="w-full px-4 sm:px-6 py-4 sm:py-6 fixed top-0 left-0 right-0 z-50 backdrop-blur-sm"
    >
      <div className="mx-auto w-full max-w-6xl">
        <div className="bg-gray-100/80 backdrop-blur-sm rounded-2xl md:rounded-full px-4 sm:px-5 py-3 flex items-center justify-between md:justify-center md:space-x-8 shadow-sm border border-white/20">
          <button
            onClick={() => {
              setIsMobileMenuOpen(false);
              onNavigateToHome && onNavigateToHome();
            }}
            className="text-lg sm:text-xl font-bold text-black hover:text-gray-700 transition-colors duration-200 whitespace-nowrap"
          >
            Valycode
          </button>

          {/* Desktop nav — Design Inspiration & Downloadable Prompts demoted to footer */}
          <nav className="hidden md:flex items-center space-x-4 lg:space-x-6">
            <button
              onClick={() => onNavigateToDashboard && onNavigateToDashboard()}
              className="text-gray-700 hover:text-black transition-colors duration-200 whitespace-nowrap"
            >
              Dashboard
            </button>
            <button
              onClick={() => onNavigateToProfile && onNavigateToProfile()}
              className="text-gray-700 hover:text-black transition-colors duration-200 whitespace-nowrap"
            >
              Profile
            </button>
            {showBuyCta && (
              <BuyButton planId="trial" context="header-nav" variant="compact" />
            )}
          </nav>

          {/* Mobile menu button */}
          <button
            type="button"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white/70 px-3 py-2 text-gray-900 hover:bg-white transition-colors"
          >
            {isMobileMenuOpen ? (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M4 6h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M4 12h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile menu panel */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="md:hidden mt-3 rounded-2xl border border-gray-200 bg-white/95 backdrop-blur-sm shadow-sm overflow-hidden"
            >
              <div className="p-2">
                <button
                  onClick={() => { setIsMobileMenuOpen(false); onNavigateToDashboard && onNavigateToDashboard(); }}
                  className="w-full text-left px-4 py-3 rounded-xl text-gray-800 hover:bg-gray-50 transition-colors"
                >
                  Dashboard
                </button>
                <button
                  onClick={() => { setIsMobileMenuOpen(false); onNavigateToProfile && onNavigateToProfile(); }}
                  className="w-full text-left px-4 py-3 rounded-xl text-gray-800 hover:bg-gray-50 transition-colors"
                >
                  Profile
                </button>
                {showBuyCta && (
                  <div className="px-2 pt-2">
                    <BuyButton
                      planId="trial"
                      context="header-mobile"
                      variant="primary"
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header; 